import { css } from '@emotion/react';
import React, {
  forwardRef, type PropsWithChildren, type ToggleEventHandler,
  useCallback, useId,
} from 'react';
import { isNullsy } from '~/_shared/utils/typeGuards';

const accordionItemStyles = css({
  overflow: 'hidden',
  counterIncrement: 'accordion-item',

  '&::details-content': {
    blockSize: 0,
    transition: 'block-size .2s ease-in-out, visibility .2s, content-visibility .2s',
    transitionBehavior: 'allow-discrete',
  },
  '&[open]::details-content': {
    blockSize: 'auto',
  },
});

const lockedAttribute = { 'data-is-locked': true };
const controlledAttribute = { 'data-is-controlled': true };

type DetailsProps = React.DetailedHTMLProps<React.DetailsHTMLAttributes<HTMLDetailsElement>, HTMLDetailsElement>;

export const AccordionItem = forwardRef<HTMLDetailsElement, PropsWithChildren<{
  readonly isLocked?: boolean;
} & DetailsProps>>(({ children, isLocked, onToggle, open, ...props }, ref) => {
  const accordionItemId = useId();
  const toggleHandler: ToggleEventHandler<HTMLDetailsElement> = useCallback((event) => {
    const targetElement = event.target as HTMLDetailsElement;
    const targetElementAccordionItemId = targetElement.getAttribute('data-accordion-element-id');
    if (targetElementAccordionItemId === accordionItemId) {
      const isControlled = !isNullsy(open);
      if (isControlled) {
      // Enforce state if in controlled mode.
        (event.target as any).open = open;
      }

      const newState: 'open' | 'closed' = (event.nativeEvent as any).newState;
      const imposedState = open ? 'open' : 'closed';

      if (isControlled && (newState === imposedState)) {
        event.preventDefault(); // Does nothing currently.
      }
      else if (onToggle) {
        onToggle(event);
      }
    }
  }, [accordionItemId, onToggle, open]);

  return (
    <details
      {...props}
      data-accordion-element-id={accordionItemId}
      css={accordionItemStyles}
      data-element="accordion-item"
      open={open}
      ref={ref}
      {...isLocked ? lockedAttribute : undefined}
      {...isNullsy(open) ? undefined : controlledAttribute}
      onToggle={toggleHandler}
    >
      {children}
    </details>
  );
});

AccordionItem.displayName = 'AccordionItem';
